/*
  Íconos de la app: Lucide (https://lucide.dev), paquete `lucide-react`.

  Los nombres exportados son semánticos para la app y se mantienen estables: si se cambia de
  biblioteca, solo cambia este archivo. Cada ícono admite:
  - `variant="filled"`: estado seleccionado. Lucide no tiene versiones rellenas, así que se
    marca con un trazo más grueso (como el peso semibold de SF Symbols).
  - `strokeWidth` en la escala de 24 px (2 = grosor estándar de Lucide).
  - `title`: solo si el ícono transmite información por sí solo; si no, queda oculto a lectores.
*/
import type { ComponentType } from "react";
import type { LucideIcon, LucideProps } from "lucide-react";
import {
  ArrowLeftRight as LArrowLeftRight,
  ArrowRight as LArrowRight,
  Ban as LBan,
  Bell as LBell,
  Building as LBuilding,
  Building2 as LBuilding2,
  Calculator as LCalculator,
  Calendar as LCalendar,
  Camera as LCamera,
  Check as LCheck,
  ChevronDown as LChevronDown,
  ChevronLeft as LChevronLeft,
  ChevronRight as LChevronRight,
  ChevronUp as LChevronUp,
  CircleAlert as LCircleAlert,
  CircleArrowDown as LCircleArrowDown,
  CircleArrowUp as LCircleArrowUp,
  CircleCheck as LCircleCheck,
  CircleEllipsis as LCircleEllipsis,
  CirclePlus as LCirclePlus,
  CircleX as LCircleX,
  ClipboardList as LClipboardList,
  Clock as LClock,
  CloudUpload as LCloudUpload,
  Columns3 as LColumns3,
  Contrast as LContrast,
  Copy as LCopy,
  DollarSign as LDollarSign,
  Download as LDownload,
  Ellipsis as LEllipsis,
  Eye as LEye,
  EyeOff as LEyeOff,
  FileDown as LFileDown,
  FileUp as LFileUp,
  FilePlus2 as LFilePlus2,
  FileSpreadsheet as LFileSpreadsheet,
  FileText as LFileText,
  FolderOpen as LFolderOpen,
  History as LHistory,
  House as LHouse,
  Info as LInfo,
  Languages as LLanguages,
  LayoutDashboard as LLayoutDashboard,
  List as LList,
  LoaderCircle as LLoaderCircle,
  LogOut as LLogOut,
  Mail as LMail,
  MapPinned as LMapPinned,
  Menu as LMenu,
  MessageSquareText as LMessageSquareText,
  Monitor as LMonitor,
  Moon as LMoon,
  Package as LPackage,
  PackagePlus as LPackagePlus,
  PackageSearch as LPackageSearch,
  PackageMinus as LPackageMinus,
  PackageX as LPackageX,
  Undo2 as LUndo2,
  Pencil as LPencil,
  Trash2 as LTrash2,
  ScanLine as LScanLine,
  Flashlight as LFlashlight,
  Phone as LPhone,
  Plug as LPlug,
  Plus as LPlus,
  Power as LPower,
  Printer as LPrinter,
  ScanText as LScanText,
  Search as LSearch,
  Settings as LSettings,
  ShieldAlert as LShieldAlert,
  ShieldCheck as LShieldCheck,
  SlidersHorizontal as LSlidersHorizontal,
  Sparkles as LSparkles,
  Sun as LSun,
  TrendingDown as LTrendingDown,
  TriangleAlert as LTriangleAlert,
  Truck as LTruck,
  Upload as LUpload,
  User as LUser,
  Warehouse as LWarehouse,
  UserPlus as LUserPlus,
  Users as LUsers,
  X as LX,
} from "lucide-react";

export type IconProps = Omit<LucideProps, "ref"> & {
  /** "filled" para el estado seleccionado (trazo más grueso). */
  variant?: "outline" | "filled";
  /** Si el ícono transmite información por sí solo, describirlo aquí. */
  title?: string;
};

export type AppIcon = ComponentType<IconProps>;

function createIcon(name: string, Lucide: LucideIcon): AppIcon {
  function Icon({ className = "w-5 h-5", strokeWidth, variant = "outline", title, ...rest }: IconProps) {
    const base = strokeWidth === undefined ? 1.9 : Number(strokeWidth);
    return (
      <Lucide
        {...rest}
        role={title ? "img" : undefined}
        aria-label={title}
        aria-hidden={title ? undefined : true}
        className={`shrink-0 ${className}`}
        strokeWidth={variant === "filled" ? base + 0.6 : base}
      />
    );
  }
  Icon.displayName = name;
  return Icon;
}

// Navegación
export const Home = createIcon("Home", LHouse);
export const LayoutDashboard = createIcon("LayoutDashboard", LLayoutDashboard);
export const FolderOpen = createIcon("FolderOpen", LFolderOpen);
export const Calculator = createIcon("Calculator", LCalculator);
export const Package = createIcon("Package", LPackage);
export const Truck = createIcon("Truck", LTruck);
export const Clock = createIcon("Clock", LClock);
export const Users = createIcon("Users", LUsers);
export const Building2 = createIcon("Building2", LBuilding2);
export const Building = createIcon("Building", LBuilding);
export const ShieldCheck = createIcon("ShieldCheck", LShieldCheck);
export const Settings = createIcon("Settings", LSettings);
export const Bell = createIcon("Bell", LBell);
export const Search = createIcon("Search", LSearch);
export const More = createIcon("More", LCircleEllipsis);

// Acciones
export const Plus = createIcon("Plus", LPlus);
export const PlusCircle = createIcon("PlusCircle", LCirclePlus);
export const Pencil = createIcon("Pencil", LPencil);
export const Trash2 = createIcon("Trash2", LTrash2);
export const ScanLine = createIcon("ScanLine", LScanLine);
export const Flashlight = createIcon("Flashlight", LFlashlight);
export const Eye = createIcon("Eye", LEye);
export const EyeOff = createIcon("EyeOff", LEyeOff);
export const X = createIcon("X", LX);
export const XCircle = createIcon("XCircle", LCircleX);
export const Check = createIcon("Check", LCheck);
export const CheckCircle2 = createIcon("CheckCircle2", LCircleCheck);
export const Ban = createIcon("Ban", LBan);
export const Copy = createIcon("Copy", LCopy);
export const Download = createIcon("Download", LDownload);
export const FileDown = createIcon("FileDown", LFileDown);
export const Upload = createIcon("Upload", LUpload);
export const UploadCloud = createIcon("UploadCloud", LCloudUpload);
export const FileUp = createIcon("FileUp", LFileUp);
export const Camera = createIcon("Camera", LCamera);
export const ScanText = createIcon("ScanText", LScanText);
export const FilePlus2 = createIcon("FilePlus2", LFilePlus2);
export const PackagePlus = createIcon("PackagePlus", LPackagePlus);
export const UserPlus = createIcon("UserPlus", LUserPlus);
export const LogOut = createIcon("LogOut", LLogOut);
export const Power = createIcon("Power", LPower);
export const Columns3 = createIcon("Columns3", LColumns3);
export const SlidersHorizontal = createIcon("SlidersHorizontal", LSlidersHorizontal);
export const MoreHorizontal = createIcon("MoreHorizontal", LEllipsis);
export const Loader2 = createIcon("Loader2", LLoaderCircle);
export const ArrowRight = createIcon("ArrowRight", LArrowRight);
export const Menu = createIcon("Menu", LMenu);

// Navegación interna
export const ChevronDown = createIcon("ChevronDown", LChevronDown);
export const ChevronUp = createIcon("ChevronUp", LChevronUp);
export const ChevronLeft = createIcon("ChevronLeft", LChevronLeft);
export const ChevronRight = createIcon("ChevronRight", LChevronRight);

// Contenido y estados
export const User = createIcon("User", LUser);
export const FileText = createIcon("FileText", LFileText);
export const FileSpreadsheet = createIcon("FileSpreadsheet", LFileSpreadsheet);
export const ClipboardList = createIcon("ClipboardList", LClipboardList);
export const List = createIcon("List", LList);
export const History = createIcon("History", LHistory);
export const Calendar = createIcon("Calendar", LCalendar);
export const Phone = createIcon("Phone", LPhone);
export const Mail = createIcon("Mail", LMail);
export const MapPinned = createIcon("MapPinned", LMapPinned);
export const MessageSquareText = createIcon("MessageSquareText", LMessageSquareText);
export const DollarSign = createIcon("DollarSign", LDollarSign);
export const Monitor = createIcon("Monitor", LMonitor);
export const Sun = createIcon("Sun", LSun);
export const Moon = createIcon("Moon", LMoon);
export const Contrast = createIcon("Contrast", LContrast);
export const Language = createIcon("Language", LLanguages);
export const Plug = createIcon("Plug", LPlug);
export const PackageX = createIcon("PackageX", LPackageX);
export const PackageMinus = createIcon("PackageMinus", LPackageMinus);
export const Undo2 = createIcon("Undo2", LUndo2);
export const Printer = createIcon("Printer", LPrinter);
export const PackageSearch = createIcon("PackageSearch", LPackageSearch);
export const TrendingDown = createIcon("TrendingDown", LTrendingDown);
export const ArrowLeftRight = createIcon("ArrowLeftRight", LArrowLeftRight);
export const Warehouse = createIcon("Warehouse", LWarehouse);
export const ArrowUpCircle = createIcon("ArrowUpCircle", LCircleArrowUp);
export const ArrowDownCircle = createIcon("ArrowDownCircle", LCircleArrowDown);
export const AlertTriangle = createIcon("AlertTriangle", LTriangleAlert);
export const AlertCircle = createIcon("AlertCircle", LCircleAlert);
export const Info = createIcon("Info", LInfo);
export const ShieldAlert = createIcon("ShieldAlert", LShieldAlert);
export const Sparkles = createIcon("Sparkles", LSparkles);
